import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import Ticket from "../../../interfaces/ticket/Ticket";
import { Badge, Button, Dropdown } from "react-bootstrap";
import copylink from "../../../assets/images/copylink.svg";
import ClickToCopyDiv from "../../ui/ClickToCopyContainer/ClickToCopyDiv";
import { getStyledTagColors, getStatusColors } from "../../../utilities/colorUtils";
import "../issuePage/issuepage.css"
import "../taskPage/taskpage.css";
import DOMPurify from "dompurify";
import { getTicketById, getAssignableUsers, updateTicketAssignee, closeTicket, getTicketTags, addTagToTicket, removeTagFromTicket, updateTicketStoryPoints, updateTicketPriority } from "../../../services/ticketServices/TicketService";
import { addComment } from "../../../services/commentServices/CommentService";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import CommentCard from "./CommentCard";
import CommentInput from "./CommentInput";
import AttachmentItem from './AttachmentItem';
import { Attachment, Comment } from "../../../interfaces/ticket/Comment";
import Tag from "../../../interfaces/tag/Tag";
import { getStatuses, updateTicketStatus } from "../../../services/statusServices/StatusService";
import TicketStatus from "../../../interfaces/ticket/TicketStatus";
import { User } from "../../../interfaces/User";
import { getSortedStatuses } from "../../../utilities/statusUtils";
import CloseTicketModal from "./modal/CloseTicketModal";
import Select, { SelectInstance } from 'react-select';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesUp, faAngleUp, faEquals, faAngleDown, faAnglesDown } from '@fortawesome/free-solid-svg-icons';
import useOnClickOutside from "../../../hooks/useOnClickOutside";

interface IssueSidebarProps {
    issueId: string | null;
    onClose: () => void;
    onTicketUpdate?: (updatedTicket: Ticket) => void;
}

// Custom Toggle for the Reporter Dropdown to remove the green button
const CustomUserToggle = React.forwardRef<HTMLDivElement, { children: React.ReactNode, onClick: (e: React.MouseEvent<HTMLDivElement>) => void }>(
    ({ children, onClick }, ref) => (
        <div
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            className="d-flex align-items-center gap-2 p-1 rounded cursor-pointer hover:bg-gray-100 transition-colors"
            style={{ cursor: 'pointer', userSelect: 'none' }}
        >
            {children}
        </div>
    )
);

const IssueSidebar: React.FC<IssueSidebarProps> = ({ issueId, onClose, onTicketUpdate }) => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [comments, setComments] = useState<Comment[]>([]);
    const [newComment, setNewComment] = useState("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [statuses, setStatuses] = useState<TicketStatus[]>([]);
    const [assignableUsers, setAssignableUsers] = useState<User[]>([]);
    const [isSubmittingComment, setIsSubmittingComment] = useState<boolean>(false);
    const [showCloseModal, setShowCloseModal] = useState<boolean>(false);
    const [isClosingTicket, setIsClosingTicket] = useState<boolean>(false);
    const [allTags, setAllTags] = useState<Tag[]>([]);
    const [isAddingTag, setIsAddingTag] = useState<boolean>(false);

    // New state for handling Assignee click-to-edit
    const [isEditingAssignee, setIsEditingAssignee] = useState<boolean>(false);

    const selectRef = useRef<SelectInstance<{ value: number; label: string; }>>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);

    useOnClickOutside(sidebarRef, () => {
        if (!showCloseModal) {
            onClose();
        }
    });

    const sortedStatuses = getSortedStatuses();

    const getPriorityIcon = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'highest':
                return <><FontAwesomeIcon icon={faAnglesUp} className="text-red-500" /> Highest</>;
            case 'high':
                return <><FontAwesomeIcon icon={faAngleUp} className="text-orange-500" /> High</>;
            case 'medium':
                return <><FontAwesomeIcon icon={faEquals} className="text-yellow-500" /> Medium</>;
            case 'low':
                return <><FontAwesomeIcon icon={faAngleDown} className="text-green-500" /> Low</>;
            case 'lowest':
                return <><FontAwesomeIcon icon={faAnglesDown} className="text-green-500" /> Lowest</>;
            default:
                return null;
        }
    }

    useEffect(() => {
        if (isAddingTag) {
            selectRef.current?.focus();
        }
    }, [isAddingTag]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && issueId) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [issueId, onClose]);

    const fetchTicketData = async () => {
        if (currentUser && issueId) {
            setLoading(true);
            const token = await currentUser.getIdToken();
            const [ticketData, statusData, usersData, tagsData] = await Promise.all([
                getTicketById(issueId, token, true, true),
                getStatuses(token),
                getAssignableUsers(token),
                getTicketTags(token)
            ]);

            setTicket(ticketData);
            if (ticketData?.comments) setComments(ticketData.comments);
            if (statusData) setStatuses(statusData);
            if (usersData) setAssignableUsers(usersData);
            if (tagsData) setAllTags(tagsData);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (issueId) {
            fetchTicketData();
        } else {
            setTicket(null);
        }
    }, [currentUser, issueId]);

    const handleCommentSubmit = async () => {
        if (!currentUser || !issueId || (!newComment.trim() && attachments.length === 0) || isSubmittingComment) return;
        setIsSubmittingComment(true);
        const token = await currentUser.getIdToken();

        const payload = {
            authorEmployeeId: currentUser.uid,
            body: newComment,
            attachments: attachments.length > 0 ? attachments : undefined
        };

        const createdComment = await addComment(issueId, payload, token);
        if (createdComment) {
            fetchTicketData();
            setNewComment("");
            setAttachments([]);
        }
        setIsSubmittingComment(false);
    };

    const handleFilesChange = (files: File[]) => {
        setAttachments(files);
    };

    const handleStatusChange = async (statusId: string) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalStatus = ticket.status;
        const updatedTicket = { ...ticket, status: statusId };
        setTicket(updatedTicket);

        const token = await currentUser.getIdToken();
        const success = await updateTicketStatus(token, issueId, statusId);
        if (success) {
            onTicketUpdate?.(updatedTicket);
        } else {
            setTicket(prevTicket => prevTicket ? { ...prevTicket, status: originalStatus } : null);
        }
    };

    const handleAssigneeChange = async (assigneeId: string | null) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalAssignee = ticket.assignee;
        const newAssignee = assigneeId ? assignableUsers.find(user => user.id === assigneeId) : undefined;
        const updatedTicket = { ...ticket, assignee: newAssignee };
        setTicket(updatedTicket);
        setIsEditingAssignee(false); // Exit edit mode

        const token = await currentUser.getIdToken();
        const success = await updateTicketAssignee(token, issueId, assigneeId);
        if (success) {
            onTicketUpdate?.(updatedTicket);
        } else {
            setTicket(prevTicket => prevTicket ? { ...prevTicket, assignee: originalAssignee } : null);
        }
    };

    const handleStoryPointsChange = async (storyPoints: number | null) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalStoryPoints = ticket.storyPoints;
        setTicket(prevTicket => prevTicket ? { ...prevTicket, storyPoints: storyPoints } : null);

        const token = await currentUser.getIdToken();
        const updatedTicket = await updateTicketStoryPoints(token, issueId, storyPoints);
        if (updatedTicket) {
            setTicket(updatedTicket);
            onTicketUpdate?.(updatedTicket);
        } else {
            setTicket(prevTicket => prevTicket ? { ...prevTicket, storyPoints: originalStoryPoints } : null);
        }
    };

    const handlePriorityChange = async (priority: string) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalPriority = ticket.priority;
        setTicket(prevTicket => prevTicket ? { ...prevTicket, priority: priority as "Highest" | "High" | "Medium" | "Low" | "Lowest" } : null);

        const token = await currentUser.getIdToken();
        const updatedTicket = await updateTicketPriority(token, issueId, priority);
        if (updatedTicket) {
            setTicket(updatedTicket);
            onTicketUpdate?.(updatedTicket);
        } else {
            setTicket(prevTicket => prevTicket ? { ...prevTicket, priority: originalPriority } : null);
        }
    };

    const handleCloseTicket = async (comment: string) => {
        if (!currentUser || !issueId) return;
        setIsClosingTicket(true);
        const token = await currentUser.getIdToken();
        const success = await closeTicket(token, issueId, comment);
        if (success) {
            setShowCloseModal(false);
            fetchTicketData();
        }
        setIsClosingTicket(false);
    };

    const handleAddTag = async (tagId: string) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalTags = ticket.tags;
        const tagToAdd = allTags.find(tag => tag.id.toString() === tagId);
        if (tagToAdd && !originalTags.find(t => t.id === tagToAdd.id)) { // prevent adding duplicates
            const updatedTicket = { ...ticket, tags: [...originalTags, tagToAdd] };
            setTicket(updatedTicket);

            const token = await currentUser.getIdToken();
            const success = await addTagToTicket(token, issueId, tagId);
            if (success) {
                onTicketUpdate?.(updatedTicket);
            } else {
                setTicket(prevTicket => prevTicket ? { ...prevTicket, tags: originalTags } : null);
            }
        }
    };

    const handleRemoveTag = async (tagId: string) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalTags = ticket.tags;
        const updatedTicket = { ...ticket, tags: originalTags.filter(tag => tag.id.toString() !== tagId) };
        setTicket(updatedTicket);

        const token = await currentUser.getIdToken();
        const success = await removeTagFromTicket(token, issueId, tagId);
        if (success) {
            onTicketUpdate?.(updatedTicket);
        } else {
            setTicket(prevTicket => prevTicket ? { ...prevTicket, tags: originalTags } : null);
        }
    };

    const RedirectToProfilePage = (userId: string) => {
        if (!userId) return;
        navigate(`/profile/user/${userId}`);
    };

    // Helper for Select Options
    const userOptions = [
        { value: null, label: 'Unassigned', user: null },
        ...assignableUsers.map(user => ({ value: user.id, label: user.displayName, user: user }))
    ];

    // Helper to render user options with Avatar in React-Select
    const formatOptionLabel = ({ label, user }: any) => (
        <div style={{ display: "flex", alignItems: "center" }}>
            {user ? <div style={{ width: '24px', height: '24px', marginRight: '8px' }}><AvatarImage size="xxs" userId={user.id} /></div> : null}
            <div>{label}</div>
        </div>
    );

    const allAttachments = ticket?.comments?.flatMap((c: Comment) => c.attachments) || [];

    return (
        // Added flex flex-col to parent to manage layout height
        // Added style tag to handle mobile full-width overlay
        <>
            <style type="text/css">
                {`
    @media (max-width: 768px) {
        /* Change selector from .issue-sidebar to .issue-sidebar.open */
        .issue-sidebar.open {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 100% !important;
            top: 0;
            bottom: 0;
            right: 0;
            left: 0;
            border-radius: 0 !important;
            z-index: 1050; /* Ensure it sits above other mobile elements if needed */
        }
    }
    `}
            </style>
            <div ref={sidebarRef} className={`issue-sidebar ${issueId ? 'open' : ''} flex flex-col h-full`} style={{ maxHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                {loading && <div className="p-4">Loading...</div>}
                {!loading && !ticket && issueId && <div className="p-4">Ticket not found</div>}
                {!loading && ticket && (
                    <>
                        {/* Top Bar - Fixed at top, no scrolling */}
                        <div className="top top_bar_flx remove-input-txt-border flex justify-between w-full flex-shrink-0 py-2 px-4">
                            <div className="top_bar_tittle flex gap-2">
                                <div className="my_badge">
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            className="status-dropdown-toggle"
                                            style={{
                                                ...getStatusColors(ticket.status),
                                                whiteSpace: 'normal',
                                                overflowWrap: 'anywhere'
                                            }}
                                        >
                                            {ticket.status || "-"}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {sortedStatuses.map(status => {
                                                const statusColors = getStatusColors(status.name);
                                                return (
                                                    <Dropdown.Item key={status.name} onClick={() => handleStatusChange(status.name)}>
                                                        <span
                                                            style={{
                                                                display: 'inline-block',
                                                                width: '10px',
                                                                height: '10px',
                                                                borderRadius: '50%',
                                                                backgroundColor: statusColors.backgroundColor,
                                                                marginRight: '8px',
                                                            }}
                                                        ></span>
                                                        {status.name}
                                                    </Dropdown.Item>
                                                );
                                            })}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>
                            <div className="top_bar_right">
                                <Button variant="danger" className="btn_sm" onClick={() => setShowCloseModal(true)}>Close Ticket</Button>
                                <Button variant="light" className="btn_sm" onClick={onClose}>X</Button>
                            </div>
                        </div>

                        {/* Scrollable Content Area */}
                        <div className="flex-grow-1 overflow-y-auto" style={{ overflowY: 'auto', flex: 1 }}>
                            <div className="common_card mby_30 h-auto">
                                <div className="task_given_flx">
                                    <div className="task_given_left">
                                        <div className="task_name_top">
                                            <Link to={`/issue/${ticket.publicId}`}>
                                                <h3 style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                    {ticket.title}
                                                </h3>
                                            </Link>
                                            <ClickToCopyDiv
                                                textToCopy={`${process.env.REACT_APP_CURRENT_URL ?? window.location.origin}/issue/${ticket.publicId}`}
                                                className="p-1 transition-colors duration-200 mt-2 flex"
                                            >
                                                <div className="link_copy flex gap-2 align-middle items-center hover:bg-gray-100 rounded-lg p-1">
                                                    <span>
                                                        <img src={copylink} alt="copylink" />
                                                    </span>
                                                    <p className="m-0">
                                                        Issue ID: {ticket.publicId}
                                                    </p>
                                                </div>
                                            </ClickToCopyDiv>
                                        </div>
                                    </div>
                                </div>
                                <div className="task_desc">
                                    {/* Class 'stats' removed. Added a margin-bottom class like 'mb-3' if needed */}
                                    <div className="description-container" style={{ flexDirection: 'column', display: 'flex' }}>
                                        <h3>Description</h3>
                                        <div
                                            className="description-body"
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(ticket.description || "", {
                                                    ADD_ATTR: ['data-id', 'data-value', 'data-denotation-char', 'data-index', 'class'],
                                                    ADD_TAGS: ['span'],
                                                })
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Stats Section */}
                                <div className="task_given_btm d-flex flex-wrap gap-4 align-items-start no-input-focus-outline">
                                    <div className="stats">
                                        <h3>Creation Date</h3>
                                        <p>{new Date(ticket.startDate).toLocaleDateString()}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Due Date</h3>
                                        <p>{ticket.endDate ? new Date(ticket.endDate).toLocaleDateString() : '--'}</p>
                                    </div>
                                    <div className="stats tag-stats-container">
                                        <h3>Tags</h3>
                                        <div className="tags-list remove-input-txt-border d-flex flex-wrap gap-1">
                                            {ticket.tags.map((tag: Tag) => {
                                                const { tagColor, textColor } = getStyledTagColors(tag.color, '#7e57c2');
                                                return (
                                                    <div className="my_badge" key={tag.id}>
                                                        <Badge bg="" className="me-1" style={{ backgroundColor: tagColor, color: textColor, whiteSpace: 'normal', textAlign: 'left' }}>
                                                            {tag.name.toUpperCase()}
                                                            <button onClick={() => handleRemoveTag(tag.id.toString())} className="ms-2 bg-transparent border-0 text-white">x</button>
                                                        </Badge>
                                                    </div>
                                                );
                                            })}
                                            {isAddingTag ? (
                                                <div style={{ minWidth: '150px' }}>
                                                    <Select
                                                        className="no-border-field"
                                                        ref={selectRef}
                                                        options={allTags.filter(tag => !ticket.tags.some(t => t.id === tag.id)).map(tag => ({ value: tag.id, label: tag.name }))}
                                                        onChange={(option: any) => {
                                                            if (option) {
                                                                handleAddTag(option.value.toString());
                                                            }
                                                            setIsAddingTag(false);
                                                        }}
                                                        onBlur={() => setIsAddingTag(false)}
                                                        placeholder="Add..."
                                                        isClearable
                                                        menuPortalTarget={document.body}
                                                        styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                                                    />
                                                </div>
                                            ) : (
                                                <button className="add-tag-btn" onClick={() => setIsAddingTag(true)}>+</button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="stats">
                                        <h3>Sprint</h3>
                                        <p style={{ overflowWrap: 'anywhere' }}>{ticket.sprint ? ticket.sprint.name : 'Backlog'}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Story Points</h3>
                                        <Dropdown className="my_dropdown">
                                            <Dropdown.Toggle variant="light" id="dropdown-basic">{ticket.storyPoints ?? 'None'}</Dropdown.Toggle>
                                            <Dropdown.Menu align="end">
                                                <Dropdown.Item onClick={() => handleStoryPointsChange(null)}>None</Dropdown.Item>
                                                {[0, 1, 2, 3, 5, 8, 13, 21, 34, 40].map(points => (
                                                    <Dropdown.Item key={points} onClick={() => handleStoryPointsChange(points)}>{points}</Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                    <div className="stats">
                                        <h3>Priority</h3>
                                        <Dropdown className="my_dropdown">
                                            <Dropdown.Toggle variant="light" id="dropdown-basic" style={{ whiteSpace: 'normal', textAlign: 'left' }}>{getPriorityIcon(ticket.priority)}</Dropdown.Toggle>
                                            <Dropdown.Menu align="end">
                                                {["Highest", "High", "Medium", "Low", "Lowest"].map(priority => (
                                                    <Dropdown.Item key={priority} onClick={() => handlePriorityChange(priority)}>{getPriorityIcon(priority)}</Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>

                                    {/* Assignee Section - Updated to Remove Green Button & Add Search */}
                                    <div className="stats assignee_stats no-border-field" style={{ minWidth: '200px' }}>
                                        <h3>Assignee</h3>
                                        {isEditingAssignee ? (
                                            <Select
                                                classNamePrefix="react-select"
                                                autoFocus
                                                defaultMenuIsOpen
                                                options={userOptions}
                                                formatOptionLabel={formatOptionLabel}
                                                onChange={(option: any) => handleAssigneeChange(option?.value || null)}
                                                onBlur={() => setIsEditingAssignee(false)}
                                                defaultValue={ticket.assignee ? { value: ticket.assignee.id, label: ticket.assignee.displayName, user: ticket.assignee } : { value: null, label: 'Unassigned', user: null }}
                                                placeholder="Assign..."
                                            />
                                        ) : (
                                            <div
                                                onClick={() => setIsEditingAssignee(true)}
                                                className="d-flex align-items-center gap-2 p-1 rounded cursor-pointer hover:bg-gray-100 transition-colors"
                                                style={{ cursor: 'pointer', border: '1px solid transparent' }}
                                                title="Click to change assignee"
                                            >
                                                <div style={{ width: '24px', height: '24px' }}>
                                                    <AvatarImage size="xxs" userId={ticket.assignee?.id || ""} />
                                                </div>
                                                <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                                    {ticket.assignee ? ticket.assignee.displayName : 'Unassigned'}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Reporter Section - Updated to Match IssuePage */}
                                    {ticket.reporter && (
                                        <div className="stats assignee_stats min-w-[200px]">
                                            <h3>Reporter</h3>
                                            <Dropdown className="my_dropdown w-full">
                                                <Dropdown.Toggle as={CustomUserToggle} id="dropdown-reporter">
                                                    <AvatarImage size="xxs" userId={ticket.reporter.id} />
                                                    <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                                        {ticket.reporter.firstName + " " + ticket.reporter.lastName}
                                                    </span>
                                                </Dropdown.Toggle>

                                                {/* STYLED USER CARD DROPDOWN */}
                                                <Dropdown.Menu
                                                    align="end"
                                                    className="p-0 border-0 shadow-2xl rounded-xl overflow-hidden mt-1"
                                                    style={{ zIndex: 1000 }}
                                                >
                                                    <div className="w-80 bg-white p-0">
                                                        {/* Header / Background Decoration */}
                                                        <div className="h-16 bg-gradient-to-r from-purple-100 to-blue-50 w-full"></div>

                                                        <div className="px-5 pb-5 relative flex flex-col items-center">

                                                            {/* Avatar - Centered and overlapping */}
                                                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 border-4 border-white rounded-full bg-white shadow-sm">
                                                                <AvatarImage size="md" userId={ticket.reporter.id} />
                                                            </div>

                                                            {/* User Details - Centered text */}
                                                            <div className="mt-16 mb-4 text-center">
                                                                <h3 className="text-xl font-bold text-gray-900 m-0 leading-tight">
                                                                    {ticket.reporter.firstName + " " + ticket.reporter.lastName}
                                                                </h3>
                                                                <p className="text-gray-500 text-sm m-0 break-all">
                                                                    {ticket.reporter.email}
                                                                </p>
                                                            </div>

                                                            <div className="h-px bg-gray-100 w-full mb-4"></div>

                                                            {/* Actions */}
                                                            <div className="grid grid-cols-2 gap-3 w-full">
                                                                <Button
                                                                    variant="light"
                                                                    size="sm"
                                                                    className="w-full font-medium text-gray-700 hover:bg-gray-200 border-gray-200"
                                                                >
                                                                    Message
                                                                </Button>
                                                                <Button
                                                                    variant="light"
                                                                    size="sm"
                                                                    className="w-full font-medium"
                                                                    onClick={() => RedirectToProfilePage(ticket.reporter.id || "")}
                                                                >
                                                                    Profile
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Attachments Section - Fixed Layout */}
                            <div className="common_card h-auto my-3">
                                <div className="top border_tittle">
                                    <div className="top_bar_tittle"><h2>Attachments</h2></div>
                                </div>
                                <div className="p-4">
                                    {allAttachments.length > 0 ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem' }}>
                                            {allAttachments.map((attachment: Attachment) => (
                                                <div key={attachment.id} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                    <AttachmentItem attachment={attachment} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No attachments yet.</p>
                                    )}
                                </div>
                            </div>

                            <div className="common_card h-auto">
                                <div className="top border_tittle">
                                    <div className="top_bar_tittle"><h2>Activity</h2></div>
                                </div>
                                <div className="p-4">
                                    <div className="comment-list">
                                        {comments.map((comment: Comment) => (
                                            <CommentCard key={comment.id} comment={comment} />
                                        ))}
                                    </div>
                                    <CommentInput
                                        value={newComment}
                                        onChange={setNewComment}
                                        onFilesChange={handleFilesChange}
                                        onSubmit={handleCommentSubmit}
                                        attachments={attachments}
                                        isSubmitting={isSubmittingComment}
                                        users={assignableUsers}
                                    />
                                </div>
                            </div>
                        </div>

                        <CloseTicketModal
                            show={showCloseModal}
                            onHide={() => setShowCloseModal(false)}
                            onSubmit={handleCloseTicket}
                            isSubmitting={isClosingTicket}
                        />
                    </>
                )}
            </div>
        </>
    );
};

export default IssueSidebar;